import React, {useMemo, useState} from "react";
import {Alert, Spinner} from "peggirkit";
import {FormattedMessage} from "react-intl";
import {useQuery} from "@tanstack/react-query";
import {Route} from "@/routing/Router";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {api} from "@/data/api";
import {formatLocalDate, isToday} from "@/utils/DateUtils";
import useReportDayParam from "@widgets/reports/dayReport/dayPicker/useReportDayParam";
import DayReportDayPicker from "@widgets/reports/dayReport/dayPicker/DayReportDayPicker";
import CartTimelinePageHeading from "@widgets/reports/cartTimeline/heading/CartTimelinePageHeading";
import CartTimelineStats from "@widgets/reports/cartTimeline/stats/CartTimelineStats";
import CartTimelineCard from "@widgets/reports/cartTimeline/timeline/CartTimelineCard";
import CartsTable from "@widgets/reports/cartTimeline/table/CartsTable";
import CartDetailsModal from "@widgets/reports/cartTimeline/details/CartDetailsModal";
import useNow from "@widgets/reports/cartTimeline/useNow";
import {
    CART_TIMELINE_QUERY_KEY,
    CART_TIMELINE_REFRESH_INTERVAL,
    CartInTimeline,
    CartKindFilter,
    cartKindFilters,
    matchesFilter,
    toCartsInTimeline,
} from "@widgets/reports/cartTimeline/CartTimelineUtils";

type Props = {
    route: Route,
};

/**
 * The carts (winkelwagens) of a day on a timeline, to see when customers are
 * ordering, e.g. before deploying. Today refreshes by itself.
 */
const CartTimelineOverview = ({route}: Props) => {
    const {dashboardLocation} = useRestaurant();
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedDay, setSelectedDay] = useReportDayParam();
    const [filter, setFilter] = useState<CartKindFilter>("all");
    const [showPos, setShowPos] = useState(false);
    const [selected, setSelected] = useState<CartInTimeline | null>(null);
    const day: string = formatLocalDate(selectedDay);
    const viewingToday: boolean = isToday(selectedDay);
    const now: Date = useNow(CART_TIMELINE_REFRESH_INTERVAL);
    const {data, isFetching, isPending, isError, refetch} = useQuery({
        queryKey: [CART_TIMELINE_QUERY_KEY, dashboardLocation.id, day],
        queryFn: () => api.cartTimeline.getCartsOfDay(dashboardLocation.id, day),
        // Stops refreshing after an error (e.g. signed out), until refreshed by hand
        refetchInterval: query => viewingToday && query.state.status !== "error"
            ? CART_TIMELINE_REFRESH_INTERVAL
            : false,
    });

    const allCarts: CartInTimeline[] = useMemo(() => toCartsInTimeline(data ?? [], now), [data, now]);
    const cartsWithPos: CartInTimeline[] = useMemo(
        () => allCarts.filter(c => showPos || c.kind !== "pos"),
        [allCarts, showPos]);
    const visibleCarts: CartInTimeline[] = useMemo(
        () => cartsWithPos.filter(c => matchesFilter(c.kind, filter)),
        [cartsWithPos, filter]);
    const counts = useMemo(
        () => Object.fromEntries(
            cartKindFilters.map(f => [f, cartsWithPos.filter(c => matchesFilter(c.kind, f)).length])
        ) as Record<CartKindFilter, number>,
        [cartsWithPos]);

    return (
        <>
            <CartTimelinePageHeading
                route={route}
                selectedDay={selectedDay}
                setShowDatePicker={setShowDatePicker}
                refresh={() => void refetch()}
                isFetching={isFetching}
            />

            <div className={"mt-6 md:mt-12 pb-12 flex flex-col gap-6 md:gap-12"}>
                {
                    isError
                        ? (
                            <Alert
                                type={"danger"}
                                title={<FormattedMessage id={"cartTimeline.loadFailed"}/>}
                            />
                        )
                        : isPending
                            ? (
                                <div className={"flex justify-center py-12"}>
                                    <Spinner size={"lg"}/>
                                </div>
                            )
                            : (
                                <>
                                    <CartTimelineStats carts={allCarts}/>

                                    <CartTimelineCard
                                        carts={visibleCarts}
                                        allCarts={cartsWithPos}
                                        day={selectedDay}
                                        now={viewingToday ? now : null}
                                        filter={filter}
                                        setFilter={setFilter}
                                        showPos={showPos}
                                        setShowPos={setShowPos}
                                        counts={counts}
                                        onSelect={setSelected}
                                    />

                                    <CartsTable
                                        carts={visibleCarts}
                                        onSelect={setSelected}
                                    />
                                </>
                            )
                }
            </div>

            <DayReportDayPicker
                showDatePicker={showDatePicker}
                setShowDatePicker={setShowDatePicker}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
            />

            <CartDetailsModal
                item={selected}
                onClose={() => setSelected(null)}
            />
        </>
    );
};

export default CartTimelineOverview;